import { IProviderProps } from "../interfaces/provider";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { authRedirectUri, loginRequest } from "../authConfig";
import { AccountInfo, AuthenticationResult, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { Loading } from "../components/Loading";
import { MessageBar, MessageBarType, PrimaryButton, Stack, Text } from "@fluentui/react";
import { appInsights } from "../applicationInsights";

interface IAuthContext {
    isAuthed: boolean
    getAccessToken: () => Promise<string | undefined>
    getAccount: () => AccountInfo | undefined
}

const authContext = createContext<IAuthContext>({
    isAuthed: false,
    getAccessToken: async () => (""),
    getAccount: () => undefined
})

const loginHintStorageKey = 'loginHint';
const loginHintQueryParams = ['login_hint', 'loginHint', 'username', 'userPrincipalName', 'email'];

const isInIframe = () => {
    try {
        return window.self !== window.top
    } catch {
        return true
    }
}

const asError = (error: unknown) => {
    return error instanceof Error ? error : new Error(String(error))
}

const getAuthErrorDetails = (error: unknown) => {
    const msalError = error as {
        errorCode?: string
        subError?: string
        correlationId?: string
        message?: string
    }

    return {
        message: error instanceof Error ? error.message : msalError?.message ?? String(error),
        errorCode: msalError?.errorCode,
        subError: msalError?.subError,
        correlationId: msalError?.correlationId,
    }
}

const isInteractionRequired = (error: unknown) => {
    const { errorCode } = getAuthErrorDetails(error)
    return error instanceof InteractionRequiredAuthError ||
        errorCode === 'interaction_required' ||
        errorCode === 'login_required' ||
        errorCode === 'consent_required' ||
        errorCode === 'no_tokens_found' ||
        errorCode === 'monitor_window_timeout'
}

const trackAuthError = (operation: string, error: unknown) => {
    const details = getAuthErrorDetails(error)
    appInsights.trackException({
        exception: asError(error),
        properties: {
            operation,
            inIframe: isInIframe(),
            ...details,
        }
    })
}

const getLoginHint = () => {
    try {
        const params = new URLSearchParams(window.location.search)
        const queryHint = loginHintQueryParams
            .map((name) => params.get(name))
            .find((value): value is string => !!value)

        if (queryHint) {
            localStorage.setItem(loginHintStorageKey, queryHint)
            return queryHint
        }

        return localStorage.getItem(loginHintStorageKey) ?? undefined
    } catch {
        return undefined
    }
}

const getInteractiveLoginRequest = () => {
    const loginHint = getLoginHint()

    return {
        ...loginRequest,
        redirectUri: authRedirectUri,
        ...(loginHint ? { loginHint } : {})
    }
}

const getSignInRequiredMessage = (error: unknown) => {
    if (isInteractionRequired(error)) {
        return 'Your Microsoft session needs to be refreshed. Select Sign in to continue.'
    }

    return 'We could not sign you in automatically. Select Sign in to continue.'
}

const getAutomaticPopupErrorMessage = (error: unknown) => {
    const { errorCode } = getAuthErrorDetails(error)
    if (errorCode === 'popup_window_error' || errorCode === 'empty_window_error' || errorCode === 'user_cancelled') {
        return 'Automatic sign-in could not open the Microsoft popup. Select Sign in to continue.'
    }

    return getSignInRequiredMessage(error)
}

const AuthenticationRequired = ({
    error,
    isSigningIn,
    onSignIn,
}: {
    error?: string
    isSigningIn: boolean
    onSignIn: () => void
}) => (
    <div style={{ display: "flex", justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', maxWidth: 460, textAlign: 'center', alignItems: 'center' } }}>
            <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>Sign in required</Text>
            <Text styles={{ root: { color: '#605e5c' } }}>
                Your Microsoft session needs to be refreshed. Select Sign in to continue.
            </Text>
            {error && (
                <MessageBar messageBarType={MessageBarType.error} isMultiline>
                    {error}
                </MessageBar>
            )}
            <PrimaryButton
                text={isSigningIn ? "Signing in..." : "Sign in"}
                onClick={onSignIn}
                disabled={isSigningIn}
            />
        </Stack>
    </div>
)


const AuthProvider = (props: IProviderProps) => {
    const { instance, accounts, inProgress } = useMsal()
    const isAuthenticated = useIsAuthenticated();
    const [account, setAccount] = useState<AccountInfo | undefined>(undefined)
    const [apiToken, setApiToken] = useState<string>()
    const [authError, setAuthError] = useState<string>()
    const [isLoginRequired, setIsLoginRequired] = useState(false)
    const [isSilentLoginInProgress, setIsSilentLoginInProgress] = useState(false)
    const [isSigningIn, setIsSigningIn] = useState(false)
    const silentLoginStartedRef = useRef(false)
    const automaticPopupStartedRef = useRef(false)

    const applyAuthenticationResult = useCallback((auth: AuthenticationResult) => {
        if (auth.account) {
            instance.setActiveAccount(auth.account)
            setAccount(auth.account)
        }

        if (auth.accessToken) {
            setApiToken(auth.accessToken)
            setAuthError(undefined)
            setIsLoginRequired(false)
        }
    }, [instance])

    useEffect(() => {
        const active = instance.getActiveAccount();
        if (!active && accounts.length > 0) {
            instance.setActiveAccount(accounts[0])
            setAccount(accounts[0])
        }

        if (active || accounts.length > 0) {
            setIsLoginRequired(false)
        }
    }, [accounts, instance])


    const getAccount = useCallback(() => {
        const active = instance.getActiveAccount()
        if (active) {
            return active
        }
        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0])
            setAccount(accounts[0])
            return accounts[0]
        }
        return account
    }, [account, accounts, instance])


    const getAccessToken = useCallback(async (): Promise<string | undefined> => {
        if (inProgress !== InteractionStatus.None) {
            return
        }
        const activeAccount = instance.getActiveAccount() ?? account ?? accounts[0]
        if (!activeAccount) {
            setIsLoginRequired(true)
            return
        }
        if (!instance.getActiveAccount()) {
            instance.setActiveAccount(activeAccount)
        }
        const request = {
            ...loginRequest,
            account: activeAccount,
            redirectUri: authRedirectUri,
        };
        // Silently acquires an access token which is then attached to a request for Microsoft Graph data
        try {
            const auth = await instance.acquireTokenSilent(request)
            if (auth) {
                const token = auth.accessToken
                if (!token) {
                    return
                }

                setApiToken(token);
                return token
            }
        } catch (e) {
            console.error(e)
            trackAuthError('acquireTokenSilent', e)
            setApiToken(undefined)
            setIsLoginRequired(true)

            if (isInteractionRequired(e)) {
                setAuthError(getSignInRequiredMessage(e))
                return
            }

            setAuthError(getAuthErrorDetails(e).message)
        }
    }, [account, accounts, inProgress, instance])

    const attemptPopupLogin = useCallback(async (automatic: boolean = false) => {
        if (isSigningIn || inProgress !== InteractionStatus.None) {
            return
        }

        if (automatic) {
            if (automaticPopupStartedRef.current) {
                return
            }
            automaticPopupStartedRef.current = true
        }

        setIsSigningIn(true)
        setAuthError(undefined)

        try {
            const auth = await instance.loginPopup(getInteractiveLoginRequest())
            applyAuthenticationResult(auth)

            if (!auth.accessToken && auth.account) {
                const tokenAuth = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: auth.account,
                    redirectUri: authRedirectUri,
                })
                applyAuthenticationResult(tokenAuth)
            }
        } catch (e) {
            console.error(e)
            trackAuthError(automatic ? 'loginPopupAutomatic' : 'loginPopup', e)
            setApiToken(undefined)
            setIsLoginRequired(true)
            setAuthError(automatic ? getAutomaticPopupErrorMessage(e) : getAuthErrorDetails(e).message)
        } finally {
            setIsSigningIn(false)
        }
    }, [applyAuthenticationResult, inProgress, instance, isSigningIn])

    const attemptSilentLogin = useCallback(async () => {
        if (silentLoginStartedRef.current || inProgress !== InteractionStatus.None) {
            return
        }

        silentLoginStartedRef.current = true
        setIsSilentLoginInProgress(true)
        setAuthError(undefined)

        try {
            const auth = await instance.ssoSilent(getInteractiveLoginRequest())
            applyAuthenticationResult(auth)
        } catch (e) {
            console.error(e)
            trackAuthError('ssoSilent', e)
            setApiToken(undefined)
            await attemptPopupLogin(true)
        } finally {
            setIsSilentLoginInProgress(false)
        }
    }, [applyAuthenticationResult, attemptPopupLogin, inProgress, instance])

    const signIn = useCallback(async () => {
        await attemptPopupLogin(false)
    }, [attemptPopupLogin])

    useEffect(() => {
        if (inProgress !== InteractionStatus.None || apiToken || accounts.length > 0) {
            return
        }

        attemptSilentLogin()
    }, [accounts.length, apiToken, attemptSilentLogin, inProgress])

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) {
            return
        }
        if (!apiToken && (isAuthenticated || accounts.length > 0)) {
            getAccessToken()
        }
    }, [accounts.length, apiToken, getAccessToken, inProgress, isAuthenticated])

    // inProgress is an enum value from MSAL, not a string
    if (inProgress === InteractionStatus.Startup) {
        return <Loading />
    }

    if (isSilentLoginInProgress) {
        return <Loading label={"Signing in..."} />
    }

    if (!apiToken && isLoginRequired) {
        return <AuthenticationRequired error={authError} isSigningIn={isSigningIn} onSignIn={signIn} />
    }

    if (!apiToken) {
        return <Loading />
    }

    return (
        <authContext.Provider value={{
            isAuthed: isAuthenticated && !!apiToken,
            getAccessToken,
            getAccount,
        }}>
            {props.children}
        </authContext.Provider>
    )


}

export {
    AuthProvider, authContext
}
